import React, { useState, useEffect } from 'react';
import { useEscapeClose } from '../../../utils/SamuiUtils';

const CompModal = ({
    showCompModal,
    handleCompClose,
    compDataList,
    onRowSelectComp
}) => {
    // สถานะการค้นหา
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCompDataList, setFilteredCompDataList] = useState(compDataList);
    const [selectedRowIndex, setSelectedRowIndex] = useState(0); // เพิ่ม state สำหรับเลือกแถว

    useEscapeClose(showCompModal, handleCompClose);
    
    // ฟิลเตอร์ข้อมูลเมื่อ searchTerm หรือ arDataList เปลี่ยนแปลง
    useEffect(() => {
        setFilteredCompDataList(
            compDataList.filter(ar =>
                ar.Comp_Code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ar.Comp_Name_TH.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
        setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อข้อมูลเปลี่ยน
    }, [searchTerm, compDataList]);

    useEffect(() => {
        if (showCompModal) {
            setSearchTerm('');
            setSelectedRowIndex(0); // รีเซ็ตการเลือกแถวเมื่อเปิด modal
        }
    }, [showCompModal]);

    // เพิ่ม keyboard navigation สำหรับ modal
    useEffect(() => {
        const handleKeyDown = (event) => {
            // ทำงานเฉพาะเมื่อ modal เปิดอยู่
            if (!showCompModal) return;

            // ป้องกันการทำงานถ้า focus อยู่ใน input search
            if (document.activeElement.tagName === 'INPUT') {
                // ยกเว้น Enter, ESC, F5 และ ArrowDown ที่ยังให้ทำงานได้ใน input
                if (event.key !== 'Enter' && event.key !== 'Escape' && event.key !== 'F5' && event.key !== 'ArrowDown') {
                    return;
                }
            }

            if (filteredCompDataList.length === 0) return;

            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex > 0 ? prevIndex - 1 : filteredCompDataList.length - 1;
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        scrollToRow(newIndex);
                        return newIndex;
                    });
                    break;

                case 'ArrowDown':
                    event.preventDefault();
                    // ถ้าอยู่ใน input และกดลูกศรลง ให้ focus ไปที่ตารางและเลือกแถวแรก
                    if (document.activeElement.tagName === 'INPUT') {
                        setSelectedRowIndex(0);
                        scrollToRow(0);
                        // เอา focus ออกจาก input
                        document.activeElement.blur();
                        return;
                    }
                    
                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex < filteredCompDataList.length - 1 ? prevIndex + 1 : 0;
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        scrollToRow(newIndex);
                        return newIndex;
                    });
                    break;

                case 'Enter':
                    event.preventDefault();
                    if (filteredCompDataList[selectedRowIndex]) {
                        onRowSelectComp(filteredCompDataList[selectedRowIndex]);
                    }
                    break;

                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                    console.log('ESC pressed in CompModal, closing modal');
                    handleCompClose();
                    return false;

                case 'F5':
                    event.preventDefault();
                    const searchInput = document.querySelector('.modal.show input[type="text"]');
                    
                    // ถ้า focus อยู่ใน input อยู่แล้ว ให้เอา focus ออกเพื่อใช้ลูกศรได้
                    if (document.activeElement.tagName === 'INPUT') {
                        document.activeElement.blur();
                        setSelectedRowIndex(0); // เลือกแถวแรก
                        scrollToRow(0);
                    } else {
                        // ถ้าไม่ได้ focus ใน input ให้ focus ช่องค้นหา
                        if (searchInput) {
                            searchInput.focus();
                        }
                    }
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown, true); 

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [showCompModal, filteredCompDataList, selectedRowIndex, handleCompClose]);

    // ฟังก์ชันสำหรับเลื่อน scroll ไปยังแถวที่เลือก
    const scrollToRow = (rowIndex) => {
        setTimeout(() => {
            const tableContainer = document.querySelector('.modal.show .table-responsive');
            const selectedRow = document.querySelector(`.modal.show tbody tr:nth-child(${rowIndex + 1})`);
            
            if (tableContainer && selectedRow) {
                // คำนวณตำแหน่งที่ต้องเลื่อน
                const rowTop = selectedRow.offsetTop;
                const rowHeight = selectedRow.offsetHeight;
                const containerHeight = tableContainer.clientHeight;
                const scrollTop = tableContainer.scrollTop;
                
                // ตรวจสอบว่าแถวอยู่นอกเขตมองเห็นหรือไม่
                if (rowTop < scrollTop) {
                    // แถวอยู่เหนือเขตมองเห็น - เลื่อนขึ้น
                    tableContainer.scrollTop = Math.max(0, rowTop - 20); // เผื่อ padding
                } else if (rowTop + rowHeight > scrollTop + containerHeight) {
                    // แถวอยู่ใต้เขตมองเห็น - เลื่อนลง
                    tableContainer.scrollTop = rowTop - containerHeight + rowHeight + 20; // เผื่อ padding
                }
            }
        }, 10);
    };

    return (
        <>
            <div className={`modal fade ${showCompModal ? 'show d-block' : ''}`} tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">รายชื่อบริษัท</h5>
                            <button type="button" className="close" onClick={handleCompClose}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="ค้นหา รหัสบริษัท หรือ ชื่อบริษัท"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="table-responsive" style={{ maxHeight: '650px', overflowY: 'auto' }}>
                                <table className="table table-striped table-hover">
                                    <thead className="thead-dark">
                                        <tr>
                                            <th className="text-center" style={{ width: '139px' }}>รหัสบริษัท</th>
                                            <th className="text-center" style={{ width: '620px' }}>ชื่อบริษัท</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCompDataList && filteredCompDataList.length > 0 ? (
                                            filteredCompDataList.map((comp, index) => (
                                                <tr
                                                    key={comp.Comp_Id || index}
                                                    onClick={() => {
                                                        setSelectedRowIndex(index); // อัปเดต selectedRowIndex เมื่อคลิก
                                                        onRowSelectComp(comp);
                                                    }}
                                                    style={{ 
                                                        cursor: 'pointer',
                                                        backgroundColor: index === selectedRowIndex 
                                                            ? "rgb(105, 188, 216)" 
                                                            : "transparent",
                                                        transition: "background-color 0.3s ease",
                                                    }}>
                                                    <td className="text-center">{comp.Comp_Code}</td>
                                                    <td>{comp.Comp_Name_TH}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="2" className="text-center">
                                                    <h5>ไม่พบข้อมูล</h5>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div className="text-left" style={{ fontSize: "12px", color: "#666", marginRight: "auto" }}>
                                คีย์บอร์ด: F5 (ค้นหา) | ↑↓ (เลือกบริษัท) | Enter (ยืนยัน) | ESC (ปิด)
                            </div>
                            <button className="btn btn-danger" onClick={handleCompClose}>
                                ปิด (ESC)
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {showCompModal && <div className="modal-backdrop fade show"></div>}
        </>
    );
};

export default CompModal;
